'use client';

import {
  Toaster as ChakraToaster,
  Portal,
  Text,
  Toast,
  createToaster as chakraCreateToaster,
  CreateToasterProps,
  CreateToasterReturn,
  ToasterProps,
  Box,
  HStack,
  VStack,
  StackProps,
  Icon,
  TextProps,
} from '@chakra-ui/react';
import React from 'react';
import {
  IoIosCheckmarkCircle,
  IoIosInformationCircle,
  IoIosCloseCircle,
  IoIosClose,
} from 'react-icons/io';
import { useTranslations } from 'next-intl';
import { formatCompact, marketCapValueFormat } from '@/lib/utils/number.utils';
import { keyframes } from '@emotion/react';
import { CHAIN_ID_TO_CHAIN_TOKEN } from '@/lib/config/chain';
import Decimal from 'decimal.js';

export type ToastType = 'success' | 'error' | 'info';

export interface OrderResultWsItem {
  uid: number;
  trade_type: number;
  chain_id: number;
  token_ca: string;
  swap_type: number;
  wallet_address: string;
  slippage: number;
  gas_type: number;
  status: number;
  order_cap: string;
  order_amount: string;
  order_price_base: string;
  order_value_base: string;
  order_base_price: string;
  final_cap: string;
  final_amount: string;
  final_price_base: string;
  final_value_base: string;
  final_base_price: string;
  tx_hash: string;
  dex_name: string;
  pair_ca: string;
  token_symbol: string;
}

// 订单状态枚举
export enum OrderStatus {
  OrderStatusAll = 0,
  Waiting = 1,
  Proc = 2,
  OnChain = 3, // 正在链上
  Fail = 4, // 失败
  Suc = 5, // 成功
  Cancel = 6, // 被取消
  FailTimeout = 7, // 超时
}

// 获取订单状态对应的消息和类型
function getOrderStatusInfo(
  status: number,
  isBuy: boolean,
): { message: string; type: ToastType } {
  switch (status) {
    case OrderStatus.Waiting:
      return { message: 'waiting', type: 'info' };
    case OrderStatus.Proc:
      return {
        message: isBuy ? 'proc' : 'sell_proc',
        type: 'info',
      };
    case OrderStatus.OnChain:
      return { message: 'onChain', type: 'info' };
    case OrderStatus.Fail:
      return { message: 'fail', type: 'error' };
    case OrderStatus.Suc:
      return {
        message: isBuy ? 'suc' : 'sell_suc',
        type: 'success',
      };
    case OrderStatus.Cancel:
      return { message: 'cancel', type: 'error' };
    case OrderStatus.FailTimeout:
      return { message: 'timeout', type: 'error' };
    default:
      return { message: 'unknown', type: 'info' };
  }
}

type OROptions = Parameters<CreateToasterReturn['create']>[0] & {
  orderInfo: {
    isBuy: boolean;
    finalCap: string;
    chainId: number;
    tokenSymbol: string;
    orderAmount: string;
    finalAmount: string;
    status: number;
  };
};

export interface ORCreateToasterReturn extends CreateToasterReturn {
  create(options: OROptions): string | undefined;
}

export const createToaster = (
  props: CreateToasterProps,
): ORCreateToasterReturn => {
  return chakraCreateToaster(props);
};
export const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
  duration: 3000,
});
export const OrderResultToasterContainer = ChakraToaster as React.FC<
  Omit<ToasterProps, 'children'> & {
    children: (toast: OROptions) => React.ReactNode;
  }
>;
const OrderDetailItem = ({
  title,
  value,
  ...textProps
}: { title: string; value: string } & TextProps) => {
  return (
    <HStack gap={1}>
      <Text color={'text.t3'} fontSize={'11px'} lineHeight={'14px'}>
        {title}
      </Text>
      <Text fontSize={'11px'} lineHeight={'14px'} {...textProps}>
        {value}
      </Text>
    </HStack>
  );
};

const slideInAnimation = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`;

export const OrderResultToaster = () => {
  const t = useTranslations('OrderResultToast');
  return (
    <Portal>
      <OrderResultToasterContainer
        toaster={toaster}
        insetInline={{ mdDown: '4' }}
      >
        {(toast) => {
          const {
            isBuy,
            tokenSymbol,
            orderAmount,
            finalAmount,
            finalCap,
            chainId,
            status,
          } = toast.orderInfo;
          const mainColor: StackProps['bgColor'] = isBuy
            ? 'fixed.long'
            : 'fixed.short';

          const { message, type } = getOrderStatusInfo(status, isBuy);
          const baseSymbol = CHAIN_ID_TO_CHAIN_TOKEN[chainId];

          return (
            <Toast.Root
              bg={'bg.t1'}
              pos={'relative'}
              width={'335px'}
              overflow="hidden"
              borderRadius="12px"
              p={3}
            >
              <Box
                pos="absolute"
                left={0}
                right={0}
                top={0}
                bg="transparent"
                h={1}
                _before={{
                  content: '""',
                  position: 'absolute',
                  bg: mainColor,
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: '100%',
                  animation: `${slideInAnimation} 3s ease-in-out`,
                }}
              />
              <HStack w={'full'} align={'flex-start'} gap={2}>
                <Icon
                  w={'20px'}
                  flexShrink={0}
                  fontSize={'16px'}
                  color={mainColor}
                >
                  {type === 'success' ? (
                    <IoIosCheckmarkCircle />
                  ) : type === 'error' ? (
                    <IoIosCloseCircle />
                  ) : (
                    <IoIosInformationCircle />
                  )}
                </Icon>

                <VStack align={'flex-start'} gap={2} flexGrow={1}>
                  <Text lineHeight={'12px'} fontSize={'12px'} color={'text.t1'}>
                    {tokenSymbol}:{t(message)}
                  </Text>
                  <OrderDetailItem
                    title={t(isBuy ? 'buy' : 'sell')}
                    color={mainColor}
                    value={`${formatCompact(new Decimal(orderAmount).toNumber(), 4)} ${isBuy ? baseSymbol : tokenSymbol}`}
                  />
                  <OrderDetailItem
                    title={t('transaction_market_value')}
                    color={mainColor}
                    value={`${marketCapValueFormat(finalCap)}`}
                  />
                  <OrderDetailItem
                    title={t('transaction_receive_amount')}
                    color={mainColor}
                    value={`${formatCompact(new Decimal(finalAmount).toNumber(), 4)} ${isBuy ? tokenSymbol : baseSymbol}`}
                  />
                </VStack>
                <Toast.CloseTrigger right={0} top={0} pos={'relative'}>
                  <Icon fontSize={'20px'} color={'text.t1'}>
                    <IoIosClose />
                  </Icon>
                </Toast.CloseTrigger>
              </HStack>
            </Toast.Root>
          );
        }}
      </OrderResultToasterContainer>
    </Portal>
  );
};
